import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const dimensions = [
  { label: '理论阐释', values: [68, 72, 78, 82, 88] },
  { label: '案例使用', values: [82, 78, 84, 86, 90] },
  { label: '互动设计', values: [48, 55, 68, 74, 82] },
  { label: '学生回应', values: [42, 58, 65, 72, 78] },
  { label: '价值引导', values: [76, 80, 83, 86, 90] },
  { label: '节奏管理', values: [60, 65, 72, 78, 84] },
  { label: '资料支撑', values: [58, 66, 74, 80, 85] },
];

const lessons = ['9月', '10月', '11月', '12月', '1月'];

function getColor(value: number) {
  if (value >= 88) return 'bg-primary-500 text-white';
  if (value >= 78) return 'bg-primary-300 text-primary-950';
  if (value >= 68) return 'bg-primary-200 text-primary-900';
  if (value >= 58) return 'bg-secondary-200 text-foreground-950';
  return 'bg-background-200 text-foreground-950';
}

const archiveItems = [
  { label: '多节课程时间轴', icon: 'ri-timeline-view' },
  { label: '维度热力图', icon: 'ri-grid-line' },
  { label: '成长曲线', icon: 'ri-line-chart-line' },
  { label: '优秀片段库', icon: 'ri-star-line' },
  { label: '改进建议完成情况', icon: 'ri-check-double-line' },
  { label: '培训与教研记录', icon: 'ri-book-open-line' },
];

export default function P70TeacherProfile({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 md:py-6">
        <div ref={titleRef} className="text-center mb-2 md:mb-3">
          <span className={`inline-flex items-center gap-2 text-base font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一师一档
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            看见一位教师的<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">持续成长</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            一师一档既要记录问题，也要记录改进过程和可迁移的优秀经验
          </p>
        </div>

        {/* Heatmap Table */}
        <div className={`max-w-3xl mx-auto mb-2 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-3 md:p-5 glass-liquid rounded-xl overflow-x-auto">
            <div className="min-w-[560px]">
              <div className="grid grid-cols-6 gap-1 mb-1">
                <div className="text-sm font-body font-bold text-foreground-950 px-2 py-1" />
                {lessons.map((l) => (
                  <div key={l} className="text-center text-sm font-body font-bold text-foreground-950 px-2 py-1">{l}</div>
                ))}
              </div>
              {dimensions.map((dim, di) => (
                <div key={dim.label} className="grid grid-cols-6 gap-1 mb-1">
                  <div className="text-sm font-body font-bold text-foreground-950 flex items-center px-2">{dim.label}</div>
                  {dim.values.map((v, vi) => (
                    <div
                      key={vi}
                      className={`text-center text-sm font-mono font-bold py-1.5 rounded transition-all duration-700 ${getColor(v)}`}
                      style={{ transitionDelay: `${400 + di * 80 + vi * 50}ms` }}
                    >
                      {v}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Archive Items */}
        <div className={`max-w-4xl mx-auto mb-2 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {archiveItems.map((item, i) => (
              <div
                key={item.label}
                className="flex items-center gap-2 px-3 py-2 rounded-xl glass border border-background-200/40"
                style={{ transitionDelay: `${500 + i * 80}ms` }}
              >
                <i className={`${item.icon} text-sm text-primary-600`} />
                <span className="text-sm font-body font-semibold text-foreground-950 whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-base text-foreground-950 font-body leading-relaxed">
              一师一档不是静态的人事信息，也不是简单的分数累计，而是一条能够<strong className="text-gold-700">回到具体课程和证据的成长轨迹</strong>。既记录问题，也记录已经形成的优势和进步。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}